import { HeroProps } from "./types";
import { Background } from "../Background/Background";
import { Item } from "../Item/Item";
import { Button } from "../Button/Button";
import { useMemo, isValidElement, memo } from "react";
import { cssColor } from "shared/src/utils/cssUtils";
import { getRoundedClasses, getScreenStyle, alignmentStyles, defaultOptions } from "./js/heroUtils";

/**
 * Hero component for displaying promotional banners with background images, text content, and CTAs.
 * 
 * @example
 * ```tsx
 * <Hero
 *   imageUrl="sports-backgrounds/football_1.jpg"
 *   subtext1="NFL"
 *   title="Sunday Night Football"
 *   subtext2="Tonight at 8:00 PM ET"
 *   showPrimaryCta
 *   primaryCta={{ text: "Bet Now" }}
 * />
 * ```
 * 
 * @param imageUrl - URL or path to the background image
 * @param showBackgroundImage - Whether to show the background image (default: true)
 * @param height - Minimum height of the hero container
 * @param rounded - Border radius style or per-corner configuration
 * @param subtext1 - Small text displayed above the title (eyebrow)
 * @param title - Main heading text
 * @param subtext2 - Text displayed below the title
 * @param subtext3 - Additional text below subtext2
 * @param screen - Overlay style: "dark", "normal", "light", or custom { color, opacity }
 * @param backgroundFade - Whether to show a bottom fade gradient
 * @param alignment - Content alignment within the hero
 * @param textAlign - Text alignment for content
 * @param leading - Element displayed before the content (e.g., icon)
 * @param trailing - Element displayed after the content
 * @param primaryCta - Primary CTA button props
 * @param secondaryCta - Secondary CTA button props
 * @param showPrimaryCta - Whether to show primary CTA (default: true)
 * @param showSecondaryCta - Whether to show secondary CTA (default: false)
 * @param ctaLayout - "inline" places CTAs in trailing slot, "block" places below content
 * @param ctaDisplay - "block" for normal flow, "absolute" for fixed bottom positioning
 * @param options - Styling options for text elements (colors, sizes, classes)
 */
export const Hero = memo(({ 
    imageUrl, 
    showBackgroundImage = true,
    height, 
    rounded, 
    subtext1, 
    title, 
    subtext2,
    subtext3,
    children,
    background, 
    screen = "normal", 
    backgroundFade, 
    alignment = "bottomLeft", 
    textAlign = "left",
    paddingClasses = "cui-p-md", 
    leading, 
    trailing,
    primaryCta,
    secondaryCta,
    showPrimaryCta = true,
    showSecondaryCta = false,
    ctaLayout = "block",
    ctaDisplay = "block",
    onPrimaryCtaClick,
    onSecondaryCtaClick,
    options
}: HeroProps) => {
    const roundedClass = useMemo(() => getRoundedClasses(rounded), [rounded]);
    const screenBg = useMemo(() => getScreenStyle(screen), [screen]);
    const fades = useMemo(() => backgroundFade ? [{ preset: "bottomFade" as const }] : undefined, [backgroundFade]);
    const alignmentClass = alignmentStyles[alignment];
    const isCentered = alignment === "center";

    const _options = useMemo(() => ({ ...defaultOptions, ...options }), [options]);

    const subtext1Style = useMemo(() => ({ 
        color: cssColor(_options.subtext1TextColor), 
        textAlign: _options.subtext1TextAlign || textAlign 
    }), [_options.subtext1TextColor, _options.subtext1TextAlign, textAlign]);
    
    const titleStyle = useMemo(() => ({ 
        color: cssColor(_options.titleTextColor), 
        textAlign: _options.titleTextAlign || textAlign 
    }), [_options.titleTextColor, _options.titleTextAlign, textAlign]);
    
    const subtext2Style = useMemo(() => ({ 
        color: cssColor(_options.subtext2TextColor), 
        textAlign: _options.subtext2TextAlign || textAlign 
    }), [_options.subtext2TextColor, _options.subtext2TextAlign, textAlign]);

    const subtext3Style = useMemo(() => ({ 
        color: cssColor(_options.subtext3TextColor), 
        textAlign: _options.subtext3TextAlign || textAlign 
    }), [_options.subtext3TextColor, _options.subtext3TextAlign, textAlign]);

    const primaryCtaProps = useMemo(() => ({
        text: "Learn More",
        size: "xs" as const,
        type: "primary" as const,
        onClick: onPrimaryCtaClick,
        ...primaryCta,
    }), [primaryCta, onPrimaryCtaClick]);

    const secondaryCtaProps = useMemo(() => ({
        text: "Dismiss",
        size: "xs" as const,
        type: "tertiary" as const,
        onClick: onSecondaryCtaClick,
        ...secondaryCta,
    }), [secondaryCta, onSecondaryCtaClick]);

    const ctaContent = useMemo(() => (showPrimaryCta || showSecondaryCta) ? (
        <div className="cui-flex cui-gap-xs">
            {showSecondaryCta && <Button {...secondaryCtaProps} />}
            {showPrimaryCta && <Button {...primaryCtaProps} />}
        </div>
    ) : null, [showPrimaryCta, showSecondaryCta, primaryCtaProps, secondaryCtaProps]);

    const _trailing = ctaLayout === "inline" ? ctaContent : trailing;
    
    return (
        <Background 
            image={showBackgroundImage ? imageUrl : undefined} 
            style={{ minHeight: height }}
            fades={fades}
            {...background} 
            wrapperClasses={`cui-overflow-hidden ${roundedClass} ${background?.wrapperClasses || ""} cui-flex cui-flex-col ${alignmentClass} ${paddingClasses}`}
        >
            {screenBg && <div className="cui-absolute cui-inset-0" style={{ backgroundColor: screenBg }} />}
            <Item bgColor={"neutrals-transparent" as any} className={`!cui-bg-neutrals-transparent cui-relative cui-z-10 cui-h-full ${isCentered ? "!cui-w-auto" : ""}`} leading={leading} trailing={_trailing}>
                {subtext1 && <Item.Row className={`${_options.subtext1TextSize} ${_options.subtext1Classes}`} style={subtext1Style}>{subtext1}</Item.Row>}
                {title && <Item.Row className={`${_options.titleTextSize} ${_options.titleClasses}`} style={titleStyle}>{title}</Item.Row>}
                {subtext2 && (isValidElement(subtext2) 
                    ? <Item.Row>{subtext2}</Item.Row>
                    : <Item.Row className={`${_options.subtext2TextSize} ${_options.subtext2Classes}`} style={subtext2Style}>{subtext2}</Item.Row>
                )}
                {subtext3 && <Item.Row className={`${_options.subtext3TextSize} ${_options.subtext3Classes}`} style={subtext3Style}>{subtext3}</Item.Row>}
            </Item>
            {children && <div className="cui-relative cui-z-10 cui-mt-sm">{children}</div>}
            {ctaLayout === "block" && ctaContent && (
                <div className={ctaDisplay === "absolute" 
                    ? "cui-absolute cui-bottom-0 cui-left-0 cui-right-0 cui-z-10 cui-pb-md cui-flex cui-justify-center"
                    : "cui-relative cui-z-10 cui-mt-sm cui-w-full cui-flex cui-justify-center"
                }>
                    {ctaContent}
                </div>
            )}
        </Background>
    );
});
